import { Haptics, ImpactStyle } from "@capacitor/haptics";
import {
	type RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";

interface UsePullToRefreshOptions {
	onRefresh: () => Promise<void> | void;
	threshold?: number;
	maxPull?: number;
	enabled?: boolean;
	/**
	 * Element the touch listeners attach to (the app header). Scoping them here
	 * instead of `document` is important: a non-passive `touchmove` on `document`
	 * breaks native scrolling of inner overflow containers on older Android
	 * WebViews. Touch events are captured by their touchstart target, so a pull
	 * starting on the header still fires through the whole gesture, while touches
	 * on page content never reach this listener and scroll normally.
	 */
	targetRef?: RefObject<HTMLElement | null>;
}

export function usePullToRefresh({
	onRefresh,
	threshold = 80,
	maxPull = 150,
	enabled = true,
	targetRef,
}: UsePullToRefreshOptions) {
	const [pullDistance, setPullDistance] = useState(0);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const touchStartY = useRef<number>(0);
	const scrollElement = useRef<HTMLElement | null>(null);
	const hasTriggeredHaptic = useRef<boolean>(false);

	const handleTouchStart = useCallback(
		(e: TouchEvent) => {
			if (!enabled || isRefreshing) return;

			const target = e.target as HTMLElement;

			// Only start a pull-to-refresh when the gesture begins on the app
			// header — pulling down anywhere else in the page must scroll normally.
			const startedOnHeader = target.closest('[data-app-header="true"]');
			if (!startedOnHeader) return;

			// Don't allow pull-to-refresh if touch started inside a scrollable
			// container (modal or chat).
			const isInsideScrollableContainer = target.closest(
				'[data-scrollable="true"]',
			);
			if (isInsideScrollableContainer) return;

			// Only allow pull-to-refresh when scrolled to top
			const element = scrollElement.current || document.documentElement;
			if (element.scrollTop === 0) {
				touchStartY.current = e.touches[0].clientY;
			}
		},
		[enabled, isRefreshing],
	);

	const handleTouchMove = useCallback(
		async (e: TouchEvent) => {
			if (!enabled || isRefreshing || touchStartY.current === 0) return;

			const currentY = e.touches[0].clientY;
			const diff = currentY - touchStartY.current;

			// Only allow pull down
			if (diff > 0) {
				// Prevent default scroll behavior during pull
				e.preventDefault();

				// Apply resistance to the pull (diminishing returns)
				const resistance = 0.5;
				const pullAmount = Math.min(diff * resistance, maxPull);
				setPullDistance(pullAmount);

				// Trigger haptic feedback when threshold is reached
				if (pullAmount >= threshold && !hasTriggeredHaptic.current) {
					try {
						await Haptics.impact({ style: ImpactStyle.Medium });
						hasTriggeredHaptic.current = true;
					} catch (error) {
						console.warn("Haptics not available:", error);
					}
				}
			}
		},
		[enabled, isRefreshing, threshold, maxPull],
	);

	const handleTouchEnd = useCallback(async () => {
		if (!enabled || isRefreshing || touchStartY.current === 0) return;

		// Trigger refresh if pulled past threshold
		if (pullDistance >= threshold) {
			setIsRefreshing(true);

			try {
				// Trigger strong haptic feedback on release
				await Haptics.impact({ style: ImpactStyle.Heavy });
			} catch (error) {
				console.warn("Haptics not available:", error);
			}

			try {
				await onRefresh();
			} catch (error) {
				console.error("Refresh error:", error);
			} finally {
				setIsRefreshing(false);
				setPullDistance(0);
				touchStartY.current = 0;
				hasTriggeredHaptic.current = false;
			}
		} else {
			// Reset without refreshing
			setPullDistance(0);
			touchStartY.current = 0;
			hasTriggeredHaptic.current = false;
		}
	}, [enabled, isRefreshing, pullDistance, threshold, onRefresh]);

	// Set up event listeners on the header element (not document) so the
	// non-passive touchmove never sits in the global scroll path. Falls back to
	// document only if no target is provided.
	useEffect(() => {
		if (!enabled) return;

		const target: HTMLElement | Document = targetRef?.current ?? document;
		const options: AddEventListenerOptions = { passive: false };
		// Cast via unknown: the handlers take TouchEvent while EventListener is
		// typed for the generic Event (the async ones also return a Promise).
		const onStart = handleTouchStart as unknown as EventListener;
		const onMove = handleTouchMove as unknown as EventListener;
		const onEnd = handleTouchEnd as unknown as EventListener;

		target.addEventListener("touchstart", onStart, options);
		target.addEventListener("touchmove", onMove, options);
		target.addEventListener("touchend", onEnd, options);

		return () => {
			target.removeEventListener("touchstart", onStart);
			target.removeEventListener("touchmove", onMove);
			target.removeEventListener("touchend", onEnd);
		};
	}, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd, targetRef]);

	return {
		pullDistance,
		isRefreshing,
		isPulling: pullDistance > 0,
		isThresholdReached: pullDistance >= threshold,
	};
}
